import { useState } from 'react';
import { Button, DialogActions, DialogContent, FormControl, Grid, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import {
    setPopulationActiveStep,
    setOpenAddPopulation,
    uploadMember,
    closeAddPopulationHandler,
    exportUploadedMemberDetail,
} from '../../../store/adminHealthPlan.slice';
import { localDateFormat } from '../../../utils/timeFormat';
import Loading from '../../Shared/Loading';
import { isDefaultDate, generateRandomId } from '../../../utils/common';
import b64toBlob from 'b64-to-blob';

const ImportData = (props) => {
    const { currentHealthPlan, currentPopulation, fileProcessedCount, importSuccessful, updatingPopulation, validatedMembers, uploadedMemberDetail } =
        useSelector((state: RootState) => state.adminHealthPlanSlice);
    const { file, setFile } = props;
    const dispatch = useAppDispatch();
    const newMembers = validatedMembers.filter((m) => !m.IsAlreadyExisting);
    const existingMembers = validatedMembers.filter((m) => m.IsAlreadyExisting);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    const newColumns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 130,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 160,
        },
        {
            field: 'Gender',
            headerName: 'Gender',
            minWidth: 100,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'Address1',
            headerName: 'Address1',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'Address2',
            headerName: 'Address2',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'City',
            headerName: 'City',
            minWidth: 160,
        },
        {
            field: 'State',
            headerName: 'State',
            minWidth: 100,
        },
        {
            field: 'ZipCode',
            headerName: 'ZipCode',
            minWidth: 100,
        },
        {
            field: 'MemberId',
            headerName: 'Member Id',
            minWidth: 160,
        },
        {
            field: 'PcpName',
            headerName: 'PCP Name',
            minWidth: 250,
            flex: 1,
        },
    ];
    const existingColumns: GridColDef[] = [
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            minWidth: 130,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            minWidth: 160,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            minWidth: 160,
        },
        {
            field: 'Gender',
            headerName: 'Gender',
            minWidth: 100,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            valueGetter: (value, row) => `${isDefaultDate(row.DOB) ? '-' : localDateFormat(new Date(row.DOB))}`,
        },
        {
            field: 'Status',
            headerName: 'Status',
            minWidth: 160,
        },
        {
            field: 'Population',
            headerName: 'Population',
            minWidth: 120,
        },
        {
            field: 'Address1',
            headerName: 'Address1',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'Address2',
            headerName: 'Address2',
            minWidth: 200,
            flex: 1,
        },
        {
            field: 'City',
            headerName: 'City',
            minWidth: 160,
        },
        {
            field: 'State',
            headerName: 'State',
            minWidth: 100,
        },
        {
            field: 'ZipCode',
            headerName: 'ZipCode',
            minWidth: 100,
        },
        {
            field: 'MemberId',
            headerName: 'Member Id',
            minWidth: 160,
        },
        {
            field: 'PcpName',
            headerName: 'PCP Name',
            minWidth: 250,
            flex: 1,
        },
    ];

    const exportMemberDetail = async () => {
        setDownLoading(true);
        const data = await exportUploadedMemberDetail();
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `Uploaded Member Detail - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <>
            <Loading message={`Importing...${fileProcessedCount.Percentage}%`} />

            {importSuccessful && (
                <DialogContent>
                    <FormControl fullWidth>
                        <Grid spacing={2} container p={2} sx={{ height: '65vh' }}>
                            <Grid size={6} textAlign="center" marginX={'auto'}>
                                <Typography variant="h4" paddingBottom={5}>
                                    Your file was imported successfully.
                                </Typography>
                                <Typography variant="h5">Imported Members Details</Typography>
                                {Object.entries(uploadedMemberDetail).map(([label, value], index) => (
                                    <Typography key={index} className="flex justify-between border-b pb-2">
                                        <span className="font-medium" style={{ paddingRight: '10px' }}>
                                            {label} -
                                        </span>
                                        <span className="font-bold">{value}</span>
                                    </Typography>
                                ))}
                                <div style={{ paddingTop: '15px' }}>
                                    <Button
                                        style={{ minWidth: '120px', marginLeft: 'auto' }}
                                        onClick={() => exportMemberDetail()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading}
                                    >
                                        {downLoading ? 'Exporting..' : 'Export'}
                                    </Button>
                                </div>
                                {!updatingPopulation && (
                                    <Button
                                        variant="contained"
                                        className="button-120"
                                        sx={{ marginTop: 3 }}
                                        onClick={() => dispatch(setPopulationActiveStep(3))}
                                    >
                                        Continue
                                    </Button>
                                )}
                                {updatingPopulation && (
                                    <Button
                                        variant="contained"
                                        className="button-120"
                                        sx={{ marginTop: 5 }}
                                        onClick={() => {
                                            dispatch(closeAddPopulationHandler(currentHealthPlan));
                                            setFile(null);
                                        }}
                                    >
                                        Finish
                                    </Button>
                                )}
                            </Grid>
                        </Grid>
                    </FormControl>
                </DialogContent>
            )}
            {!importSuccessful && (
                <>
                    <DialogContent>
                        <FormControl fullWidth>
                            <Grid spacing={2} container p={2} sx={{ height: '65vh' }}>
                                <Grid size={12}>
                                    <Typography variant="h4" marginBottom={3}>
                                        Review & import your data
                                    </Typography>
                                </Grid>
                                <Grid size={6}>
                                    <Typography textAlign="center" variant="h6">
                                        New Members
                                    </Typography>
                                    <div style={{ height: '55vh', width: '100%' }}>
                                        <DataGrid
                                            rows={newMembers}
                                            columns={newColumns}
                                            pageSizeOptions={[5]}
                                            getRowId={(row) => `${row.AhcccsId}-${generateRandomId()}`}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                                <Grid size={6}>
                                    <Typography textAlign="center" variant="h6">
                                        Existing Members
                                    </Typography>
                                    <div style={{ height: '55vh', width: '100%' }}>
                                        <DataGrid
                                            rows={existingMembers}
                                            columns={existingColumns}
                                            pageSizeOptions={[5]}
                                            getRowId={(row) => `${row.AhcccsId}-${generateRandomId()}`}
                                            getRowHeight={() => 'auto'}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 1,
                                                },
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        </FormControl>
                    </DialogContent>

                    <DialogActions>
                        <Button className="button-120" variant="outlined" onClick={() => dispatch(setOpenAddPopulation(false))}>
                            Close
                        </Button>
                        <Button className="button-120" variant="contained" onClick={() => dispatch(setPopulationActiveStep(1))}>
                            Previous
                        </Button>
                        <Button
                            className="button-120"
                            variant="contained"
                            onClick={() => dispatch(uploadMember(file, currentHealthPlan.Id, currentPopulation.Id))}
                        >
                            Import
                        </Button>
                    </DialogActions>
                </>
            )}
        </>
    );
};

export default ImportData;
